import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { handoff } from '../lib/api.ts';

interface Props {
  readonly messageId: string;
  readonly onClose: () => void;
}

/**
 * QR shown on the counter PC. Staff scan it with any phone, the camera
 * opens, and the photo lands on this report.
 *
 * The countdown is shown because the link is short-lived by design --
 * without it someone walks to the far end of the building, scans, and
 * gets an expired page with no idea why.
 */
export const HandoffQR = ({ messageId, onClose }: Props) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [left, setLeft] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    handoff
      .issue(messageId)
      .then((h) => {
        setLeft(h.expiresIn);
        if (canvas.current) {
          // Absolute URL: the phone is not on this page, it is scanning
          // a code and needs a whole address.
          void QRCode.toCanvas(
            canvas.current,
            window.location.origin + h.url,
            { width: 232, margin: 1 },
          );
        }
      })
      .catch(() => setFailed(true));
  }, [messageId]);

  useEffect(() => {
    if (left <= 0) return;
    const id = setInterval(() => setLeft((n) => Math.max(n - 1, 0)), 1000);
    return () => clearInterval(id);
  }, [left > 0]);

  const mins = Math.floor(left / 60);
  const secs = left % 60;

  return (
    <div className="qrPanel">
      {failed ? (
        <p className="err">Could not create a photo link.</p>
      ) : (
        <>
          <p className="mono">Scan with any phone camera</p>
          <canvas ref={canvas} className="qr" />
          <p className="hint">
            {left > 0
              ? `Link expires in ${mins}:${String(secs).padStart(2, '0')}`
              : 'Expired - close and try again'}
          </p>
        </>
      )}
      <button onClick={onClose}>Close</button>
    </div>
  );
};
